/**
 * Public live trip page — h-plate.com/trip/<token>
 *
 * The receiving end of the app's "Share my trip" button: family
 * anywhere in the world watches the bus move across Dominica in real
 * time, no app or account required.
 *
 * Data: the anon-callable `get_shared_trip` RPC, polled every 4s.
 * It returns a minimal projection (driver first name, vehicle, plate,
 * live position, ride status, stop names) — never the passenger's
 * name or location. Links expire ~6h after creation or when the ride
 * ends, at which point this page shows a friendly "trip ended" state.
 *
 * Like the app, the bus glides along the road between fixes and the
 * road route to the next stop (pickup, then drop-off) shrinks as the
 * bus drives it.
 *
 * Standalone chrome (no admin layout, no marketing navbar) so the page
 * is instantly legible on a phone opened from WhatsApp.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { MapView } from '@/components/Map';
import { Bus, CheckCircle2, MapPin, ShieldCheck } from 'lucide-react';
import { distanceM, pointAlong, remainingPath, roadBetween, type LatLng } from '@/lib/tripRoute';
import TripRiderPanel, { type SharedTripRider } from '@/components/TripRiderPanel';

interface SharedTripDriver {
  first_name: string;
  vehicle: string;
  license_plate: string;
  lat: number | null;
  lng: number | null;
  location_updated_at: string | null;
}

interface SharedTrip {
  ok: boolean;
  error?: string;
  status?: string;
  origin_name?: string;
  destination_name?: string;
  picked_up_at?: string | null;
  completed_at?: string | null;
  origin_point?: { lat: number; lng: number } | null;
  destination_point?: { lat: number; lng: number } | null;
  driver?: SharedTripDriver | null;
  /** Present when the ride was booked for someone else; this link was sent to them. */
  rider?: SharedTripRider | null;
}

const POLL_MS = 4_000;
/** A position older than this is called out so family don't trust a frozen dot. */
const STALE_AFTER_MS = 2 * 60_000;
/** Glide time bounds between two fixes. */
const MIN_GLIDE_MS = 800;
const MAX_GLIDE_MS = 6_000;
/** A jump longer than this is a GPS reset, not driving: move without gliding. */
const TELEPORT_M = 3_000;
/** Fetch the road again once the bus is this far off it (detour, wrong turn). */
const REROUTE_OFF_M = 150;
const REROUTE_MIN_GAP_MS = 30_000;

type Leg = 'pickup' | 'dropoff';

/** Road route from `from` to `to`, or null when Directions is unavailable. */
function fetchRoadRoute(from: LatLng, to: LatLng): Promise<LatLng[] | null> {
  return new Promise((resolve) => {
    if (!window.google?.maps?.DirectionsService) {
      resolve(null);
      return;
    }
    new window.google.maps.DirectionsService().route(
      { origin: from, destination: to, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        const path = status === 'OK' ? result?.routes?.[0]?.overview_path : null;
        resolve(path && path.length > 1 ? path.map((p) => ({ lat: p.lat(), lng: p.lng() })) : null);
      },
    );
  });
}

const BUS_MARKER_SVG =
  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#06281d" stroke-width="2.2" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M15 6v6"/>' +
  '<path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/>' +
  '<circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>';

/** "just now", "45s ago", "3 min ago" for the last bus position. */
function agoLabel(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null;
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  if (ms < 15_000) return 'just now';
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  return `${Math.round(ms / 60_000)} min ago`;
}

export default function Trip({ token }: { token: string }) {
  const [trip, setTrip] = useState<SharedTrip | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const stopMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  /** Which leg the camera was last framed for, so it reframes once at pickup. */
  const framedForRef = useRef<string | null>(null);
  /** Where the bus is drawn right now (mid-glide this lags the latest fix). */
  const shownPosRef = useRef<LatLng | null>(null);
  const lastFixRef = useRef<{ pos: LatLng; at: number } | null>(null);
  const glideFrameRef = useRef<number | null>(null);
  const routeRef = useRef<{ leg: Leg; path: LatLng[] | null; fetchedAt: number } | null>(null);
  const routeFetchingRef = useRef(false);
  const routeLineRef = useRef<google.maps.Polyline | null>(null);
  const routeCasingRef = useRef<google.maps.Polyline | null>(null);
  const targetStopRef = useRef<LatLng | null>(null);

  const fetchTrip = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_shared_trip', {
      p_token: token,
    });
    if (error) {
      setLoadFailed(true);
      return;
    }
    setLoadFailed(false);
    setTrip(data as SharedTrip);
  }, [token]);

  useEffect(() => {
    void fetchTrip();
    const id = setInterval(() => void fetchTrip(), POLL_MS);
    return () => clearInterval(id);
  }, [fetchTrip]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  /** Draw the bus at `pos` and start the route line from it. */
  const drawBusAt = useCallback((pos: LatLng) => {
    shownPosRef.current = pos;
    if (markerRef.current) markerRef.current.position = pos;
    const line = routeLineRef.current;
    const casing = routeCasingRef.current;
    if (!line || !casing) return;
    const route = routeRef.current?.path;
    const stop = targetStopRef.current;
    const ahead = route ? remainingPath(route, pos).path : stop ? [pos, stop] : [];
    line.setPath(ahead);
    casing.setPath(route ? ahead : []);
  }, []);

  /** Glide the bus from where it is drawn to `to`, along the road when possible. */
  const glideTo = useCallback(
    (to: LatLng, durationMs: number) => {
      if (glideFrameRef.current != null) cancelAnimationFrame(glideFrameRef.current);
      glideFrameRef.current = null;
      const from = shownPosRef.current;
      if (!from || durationMs <= 0 || distanceM(from, to) > TELEPORT_M) {
        drawBusAt(to);
        return;
      }
      const road = roadBetween(routeRef.current?.path ?? [], from, to);
      const startedAt = performance.now();
      const step = (t: number) => {
        const f = Math.min(1, (t - startedAt) / durationMs);
        drawBusAt(pointAlong(road, f));
        glideFrameRef.current = f < 1 ? requestAnimationFrame(step) : null;
      };
      glideFrameRef.current = requestAnimationFrame(step);
    },
    [drawBusAt],
  );

  useEffect(
    () => () => {
      if (glideFrameRef.current != null) cancelAnimationFrame(glideFrameRef.current);
    },
    [],
  );

  // Keep the bus, stop pin and route line in sync with the latest fix.
  useEffect(() => {
    const pos =
      trip?.ok && trip.driver?.lat != null && trip.driver?.lng != null
        ? { lat: trip.driver.lat, lng: trip.driver.lng }
        : null;
    if (!map || !pos || !window.google?.maps?.marker) return;

    if (markerRef.current && markerRef.current.map !== map) {
      markerRef.current.map = null;
      markerRef.current = null;
      if (stopMarkerRef.current) stopMarkerRef.current.map = null;
      stopMarkerRef.current = null;
      routeLineRef.current?.setMap(null);
      routeCasingRef.current?.setMap(null);
      routeLineRef.current = null;
      routeCasingRef.current = null;
      routeRef.current = null;
      shownPosRef.current = null;
      lastFixRef.current = null;
      framedForRef.current = null;
    }
    if (!routeLineRef.current) {
      routeCasingRef.current = new window.google.maps.Polyline({
        map,
        strokeColor: '#064e3b',
        strokeOpacity: 0.9,
        strokeWeight: 10,
        zIndex: 1,
        clickable: false,
      });
      routeLineRef.current = new window.google.maps.Polyline({
        map,
        strokeColor: '#10b981',
        strokeOpacity: 1,
        strokeWeight: 6,
        zIndex: 2,
        clickable: false,
      });
    }
    if (!markerRef.current) {
      const el = document.createElement('div');
      el.style.cssText =
        'display:flex;align-items:center;justify-content:center;width:44px;height:44px;' +
        'border-radius:9999px;background:#10b981;border:3px solid #ffffff;' +
        'box-shadow:0 4px 14px rgba(0,0,0,0.35);';
      el.innerHTML = BUS_MARKER_SVG;
      markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: pos,
        content: el,
        title: 'H-Plate bus',
        zIndex: 3,
      });
    }
    const leg: Leg = trip?.status === 'in_progress' ? 'dropoff' : 'pickup';
    const stop = (leg === 'dropoff' ? trip?.destination_point : trip?.origin_point) ?? null;
    targetStopRef.current = stop;
    if (stop) {
      const pinnedPickup = trip?.rider?.pickup_pinned ? trip.rider.pickup_label : null;
      const stopName =
        (leg === 'dropoff' ? trip?.destination_name : pinnedPickup || trip?.origin_name) ?? '';
      const chipText = `${leg === 'dropoff' ? 'Drop-off' : 'Pickup'} · ${stopName}`;
      if (!stopMarkerRef.current) {
        const pin = document.createElement('div');
        pin.style.cssText =
          'display:flex;flex-direction:column;align-items:center;transform:translateY(8px);';
        const chip = document.createElement('div');
        chip.style.cssText =
          'background:#111827;color:#ffffff;font:600 12px/1 system-ui,sans-serif;' +
          'padding:6px 10px;border-radius:9999px;white-space:nowrap;' +
          'box-shadow:0 2px 8px rgba(0,0,0,0.35);margin-bottom:4px;';
        const dot = document.createElement('div');
        dot.style.cssText =
          'width:16px;height:16px;border-radius:9999px;background:#111827;' +
          'border:4px solid #ffffff;box-shadow:0 2px 6px rgba(0,0,0,0.35);';
        pin.append(chip, dot);
        stopMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: stop,
          content: pin,
          zIndex: 1,
        });
      } else {
        stopMarkerRef.current.position = stop;
      }
      const chipEl = (stopMarkerRef.current.content as HTMLElement | null)?.firstElementChild;
      if (chipEl) chipEl.textContent = chipText;
      stopMarkerRef.current.title = stopName;
    }

    // Road route for this leg. Fetched once per leg, and again if the bus
    // wanders off it (detour or a different road).
    const route = routeRef.current;
    const offRoute =
      route?.path != null && remainingPath(route.path, pos).offRouteM > REROUTE_OFF_M;
    const needsRoute =
      stop != null &&
      (route?.leg !== leg ||
        (offRoute && Date.now() - (route?.fetchedAt ?? 0) > REROUTE_MIN_GAP_MS));
    if (needsRoute && !routeFetchingRef.current) {
      if (route?.leg !== leg) routeRef.current = { leg, path: null, fetchedAt: 0 };
      routeFetchingRef.current = true;
      void fetchRoadRoute(pos, stop).then((path) => {
        routeFetchingRef.current = false;
        routeRef.current = { leg, path, fetchedAt: Date.now() };
        const line = routeLineRef.current;
        if (line) {
          line.setOptions(
            path
              ? { strokeOpacity: 1, icons: [] }
              : {
                  strokeOpacity: 0,
                  icons: [
                    {
                      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#10b981', scale: 4 },
                      offset: '0',
                      repeat: '16px',
                    },
                  ],
                },
          );
        }
        if (shownPosRef.current) drawBusAt(shownPosRef.current);
      });
    }

    // Glide over the real gap between the driver's GPS fixes so the bus
    // keeps moving until the next one lands, instead of jumping.
    const fixAt = trip?.driver?.location_updated_at
      ? new Date(trip.driver.location_updated_at).getTime()
      : Date.now();
    const last = lastFixRef.current;
    if (!last) {
      lastFixRef.current = { pos, at: fixAt };
      drawBusAt(pos);
    } else if (last.at !== fixAt || distanceM(last.pos, pos) > 1) {
      const gap = Number.isFinite(fixAt - last.at) ? fixAt - last.at : POLL_MS;
      lastFixRef.current = { pos, at: fixAt };
      glideTo(pos, Math.max(MIN_GLIDE_MS, Math.min(MAX_GLIDE_MS, gap)));
    } else if (shownPosRef.current) {
      drawBusAt(shownPosRef.current);
    }

    if (framedForRef.current !== leg) {
      framedForRef.current = leg;
      if (stop) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(pos);
        bounds.extend(stop);
        map.fitBounds(bounds, { top: 88, right: 72, bottom: 72, left: 72 });
        window.google.maps.event.addListenerOnce(map, 'idle', () => {
          if ((map.getZoom() ?? 0) > 16) map.setZoom(16);
        });
      } else {
        map.setCenter(pos);
        map.setZoom(14);
      }
    } else if (!map.getBounds()?.contains(pos)) {
      map.panTo(pos);
    }
  }, [trip, map, drawBusAt, glideTo]);

  const active =
    trip?.ok && (trip.status === 'accepted' || trip.status === 'in_progress');
  const ended = trip?.ok && !active;
  const notFound = trip !== null && !trip.ok;
  const completedAt = trip?.completed_at
    ? new Date(trip.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const seen = agoLabel(trip?.driver?.location_updated_at, now);
  const stale =
    !!trip?.driver?.location_updated_at &&
    now - new Date(trip.driver.location_updated_at).getTime() > STALE_AFTER_MS;
  const rider = trip?.ok ? trip.rider ?? null : null;
  const statusLine =
    trip?.status === 'in_progress'
      ? `On board, heading to ${trip.destination_name ?? (rider ? 'your stop' : 'their stop')}`
      : rider
        ? `Bus on the way to ${trip?.origin_name ?? 'your stop'}`
        : `Bus on the way to the pickup at ${trip?.origin_name ?? 'their stop'}`;

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Brand header */}
      <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
          <Bus className="w-5 h-5 text-gray-950" />
        </div>
        <div>
          <p className="font-extrabold leading-tight">H-Plate</p>
          <p className="text-[11px] text-white/50 leading-tight">Live trip sharing</p>
        </div>
        {active && !loadFailed ? (
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            LIVE
          </span>
        ) : null}
      </header>

      {notFound || loadFailed ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <MapPin className="w-10 h-10 text-white/30 mb-3" />
          <h1 className="text-xl font-bold">
            {loadFailed ? 'Connection problem' : 'This trip link has expired'}
          </h1>
          <p className="text-white/50 mt-2 text-sm max-w-sm">
            {loadFailed
              ? 'We could not reach H-Plate. Check your connection and refresh.'
              : 'Trip links stop working a few hours after they are shared. Ask your rider to send a fresh link.'}
          </p>
        </div>
      ) : trip === null ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/50 text-sm">Loading trip…</p>
        </div>
      ) : (
        <>
          {/* Trip summary card */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-sm text-white/60">
              {trip.origin_name} <span className="text-white/30">→</span>{' '}
              <span className="text-white font-semibold">{trip.destination_name}</span>
            </p>
            {ended ? null : (
              <>
                <p className="mt-1 text-base font-bold text-emerald-400">{statusLine}</p>
                {trip.driver ? (
                  <p className="mt-1 text-sm text-white/80">
                    {trip.driver.first_name}
                    {' · '}
                    {trip.driver.vehicle}
                    {' · '}
                    <span className="font-mono font-bold text-amber-300">
                      {trip.driver.license_plate}
                    </span>
                  </p>
                ) : null}
                {seen ? (
                  <p className={`mt-1 text-xs ${stale ? 'text-amber-300' : 'text-white/40'}`}>
                    {stale ? `Bus last seen ${seen}. It may be out of signal.` : `Updated ${seen}`}
                  </p>
                ) : null}
              </>
            )}
          </div>

          {rider ? (
            <TripRiderPanel
              token={token}
              rider={rider}
              status={trip.status}
              originName={trip.origin_name}
              pickedUpAt={trip.picked_up_at}
              now={now}
              onChanged={() => void fetchTrip()}
            />
          ) : null}

          {/* Live map */}
          {active ? (
            <div className={`flex-1 relative ${rider ? 'min-h-[320px]' : 'min-h-[420px]'}`}>
              <MapView
                className="absolute inset-0 w-full h-full"
                minimalControls
                onMapReady={setMap}
              />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
              {trip.status === 'completed' ? (
                <>
                  <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-4" />
                  <h1 className="text-2xl font-extrabold">Trip complete</h1>
                  <p className="text-white/60 mt-2 text-sm max-w-sm">
                    {rider ? 'You arrived at ' : 'Your rider arrived at '}
                    {trip.destination_name ?? (rider ? 'your stop' : 'their stop')}
                    {completedAt ? ` at ${completedAt}` : ''}.
                  </p>
                </>
              ) : (
                <>
                  <MapPin className="w-12 h-12 text-white/30 mb-4" />
                  <h1 className="text-xl font-bold">This trip has ended</h1>
                  <p className="text-white/50 mt-2 text-sm max-w-sm">
                    The ride is no longer active, so live location sharing has stopped.
                  </p>
                </>
              )}
            </div>
          )}

          <footer className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <p className="text-[11px] text-white/40">
              {rider
                ? 'Sent from the H-Plate app by the person who booked your ride. They get a message when you are picked up and dropped off.'
                : ended
                  ? 'Shared by the rider from the H-Plate app. Live location sharing stopped when the trip ended.'
                  : 'Shared by the rider from the H-Plate app. The bus position updates every few seconds, and live location stops when the trip ends.'}
            </p>
          </footer>
        </>
      )}
    </div>
  );
}
